import { Link } from "react-router-dom";
import ContentPage, { Eyebrow, PageTitle, Lead, H2, P } from "@/components/ContentPage";
import { useSeo } from "@/lib/seo";

export default function RetirementGuide() {
  useSeo({
    title: "Retirement Readiness Guide | Income, Taxes, Investments & Protection",
    description: "Learn the major areas to review as retirement approaches, including spending, income sources, taxes, withdrawals, investments, inflation, insurance, and estate planning.",
    canonicalPath: "/retirement-readiness-guide",
  });
  return (
    <ContentPage
      cta="Take the Retirement Readiness Assessment"
      breadcrumbs={[
        { to: "/", label: "Home" },
        { to: "/retirement-readiness-guide", label: "Retirement Readiness Guide" },
      ]}
      readMinutes={8}
    >
      <Eyebrow>Guide · Retirement</Eyebrow>
      <PageTitle>Retirement Readiness: What Should You Be Reviewing?</PageTitle>
      <Lead>
        As retirement moves from a distant idea to a nearer decision, the useful questions shift. Instead of "am I saving enough?"
        the more relevant questions become "what will spending look like?" and "how will different accounts and income sources fit together?"
      </Lead>

      <H2>Estimate retirement spending</H2>
      <P>
        Retirement spending is the anchor for most other decisions. An estimate is enough — housing, food, transportation, healthcare,
        travel, insurance, and everyday expenses — but having a number to work from is worth more than a vague sense.
      </P>

      <H2>Identify predictable income sources</H2>
      <P>
        Predictable income typically comes from sources such as Social Security or pensions, and contractual lifetime income where it exists.
        Investment withdrawals from savings and portfolios are not a source of predictable lifetime income in the same sense — they depend on the account balance
        and how the market performs.
      </P>

      <H2>Understand the remaining income need</H2>
      <P>
        Whatever spending is not covered by predictable income typically has to come from savings and investments. The gap does not have to be alarming —
        it simply becomes the amount your portfolio needs to be organized around.
      </P>

      <H2>Build a withdrawal strategy</H2>
      <P>
        Deciding which accounts to draw from, in what order, is a real decision. It affects taxes, the sustainability of the plan, and how
        different accounts interact. This guide does not prescribe a specific sequence; the point is that having some coordinated approach
        is worth more than drawing from whichever account is easiest.
      </P>

      <H2>Consider inflation</H2>
      <P>
        Even modest inflation compounds over a long retirement. Considering whether the plan accounts for expenses rising over time —
        without claiming a specific rate — is more realistic than assuming today's expenses will hold.
      </P>

      <H2>Review longevity</H2>
      <P>
        Plans that only go through a "typical" life expectancy can underestimate how long retirement may last. Looking at what the plan
        looks like at age 90–95 or beyond is a useful stress point.
      </P>

      <H2>Review Required Minimum Distributions</H2>
      <P>
        Tax-deferred accounts eventually require withdrawals under Required Minimum Distribution rules. Understanding when they begin and how they
        interact with other income can affect tax planning for the surrounding years. This is informational only.
      </P>

      <H2>Evaluate Roth planning</H2>
      <P>
        Roth strategies — including conversions — may be worth evaluating depending on individual circumstances, income patterns, and future tax exposure.
        No conversion is recommended here; only that it is worth understanding whether an evaluation has taken place.
      </P>

      <H2>Review investment risk and liquidity</H2>
      <P>
        As retirement approaches, both the amount of investment risk and the accessibility of short-term funds deserve a real look.
        Neither "all safe" nor "unchanged from your 30s" is automatically right — the fit depends on your goals and plan.
      </P>

      <H2>Consider long-term care and survivor planning</H2>
      <P>
        How extended-care costs might be handled and what the plan looks like if one spouse passes away first are important
        conversations. They are also often deferred.
      </P>

      <H2>Review beneficiaries and estate documents</H2>
      <P>
        Beneficiary designations, wills, financial and healthcare powers of attorney, and any trust or intentional legacy plans
        should reflect what you currently want. Small oversights here can quietly change important outcomes.
      </P>

      <P className="mt-10">
        Curious how your plan looks across all of these areas?{" "}
        <Link to="/assessment" className="underline" style={{ color: "#258FC4" }}>Take the Retirement Readiness Assessment</Link>.
      </P>
    </ContentPage>
  );
}
